// Phase 33 — Sparkline component.
//
// Lightweight wrapper around Recharts' LineChart with no axes/labels —
// just the stroke. Lazy-imports Recharts so dashboards that don't render
// any sparkline don't pay the bundle cost.
//
// Color rule (per phase spec):
//   trend up   -> emerald-500
//   trend down -> rose-500
//   flat       -> slate-400
//
// Usage:
//   <Sparkline data={[{v:1},{v:3},{v:2},{v:5}]} height={40} />
//   <Sparkline data={trend.cash_flow_30d} valueKey="v" labelKey="d" />

import React, { Suspense, lazy, useMemo } from "react";

const LazyChart = lazy(() => import("./SparklineInner"));

export default function Sparkline({
  data = [],
  valueKey = "v",
  labelKey = "d",
  height = 40,
  width,
  color,
  testid,
}) {
  // Empty-state fallback: render a stable, neutral baseline so KPI cards
  // don't visibly jump when the trends payload is still loading.
  const safe = useMemo(
    () => (Array.isArray(data) ? data.filter((p) => p && typeof p[valueKey] === "number") : []),
    [data, valueKey],
  );

  const trendColor = useMemo(() => {
    if (color) return color;
    if (safe.length < 2) return "rgb(148 163 184)"; // slate-400
    const first = safe[0][valueKey];
    const last = safe[safe.length - 1][valueKey];
    const delta = last - first;
    const range = Math.max(Math.abs(first), Math.abs(last), 1);
    const relative = delta / range;
    if (relative > 0.02) return "rgb(16 185 129)"; // emerald-500
    if (relative < -0.02) return "rgb(244 63 94)"; // rose-500
    return "rgb(148 163 184)"; // slate-400
  }, [safe, color, valueKey]);

  if (safe.length === 0) {
    return (
      <div
        data-testid={testid || "sparkline-empty"}
        style={{ height, width: width || "100%" }}
        className="rounded bg-slate-100/60"
      />
    );
  }

  return (
    <div data-testid={testid || "sparkline"} style={{ height, width: width || "100%" }}>
      <Suspense fallback={<div className="h-full w-full animate-pulse rounded bg-slate-100/60" />}>
        <LazyChart
          data={safe}
          valueKey={valueKey}
          labelKey={labelKey}
          color={trendColor}
        />
      </Suspense>
    </div>
  );
}
